import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ConfigService } from '../../../core/config/config-service';
import { FilterUtilsService } from '../../../features/common-data/services/filter-utils.service';
import { VendorCatalogRequest, VendorCatalogRequestMessageWrite, VendorCatalogRequestMessage, VendorCatalogRequestWrite } from 'src/app/shared/models/request.model';
import { TableQueryParams } from 'src/app/shared/models/table-query-params.model';


@Injectable({
  providedIn: 'root',
})
export class RequestService {
  private filterUtilService = inject(FilterUtilsService);
    
  constructor(private http: HttpClient, private configService: ConfigService) {}

  private get baseUrl(): string {
    return `${this.configService.getUrlVendorCatalogApi()}/requests`;
  }


  fetchPagedRequests(queryParams?: TableQueryParams, params?: {
    integrationId?: number;
    clientId?: number;
    vendorId?: number;
    includeOnlyLastMessage?: boolean;
  }): Observable<{totalCount: number, items: VendorCatalogRequest[]}> {

    var filters = queryParams?.filters ? this.filterUtilService.mapFiltersForApi(queryParams.filters): null;

    let httpParams = new HttpParams()
    .set('page', queryParams!.page)
    .set('size', queryParams!.size)
    .set('sort', (queryParams!.sort ?? '').toString())
    .set('sortDir', (queryParams!.sortOrder ?? 1).toString())
    .set('search', (queryParams!.globalFilter ?? '').toString())
    .set('filters', (JSON.stringify(filters) ?? '').toString());

    if (params?.integrationId)
      httpParams = httpParams.set('integrationId', params.integrationId);
    if (params?.clientId)
      httpParams = httpParams.set('clientId', params.clientId);
    if (params?.vendorId)
      httpParams = httpParams.set('vendorId', params.vendorId);
    if (params?.includeOnlyLastMessage)
      httpParams = httpParams.set('includeOnlyLastMessage', params.includeOnlyLastMessage);

     

     

    return this.http.get<{totalCount: number, items: VendorCatalogRequest[]}>(`${this.baseUrl}/paged`, { params: httpParams });
  }

  fetchRequestById(requestId: number): Observable<VendorCatalogRequest> {
    return this.http.get<VendorCatalogRequest>(`${this.baseUrl}/${requestId}`);
  }
  
  fetchRequestByIntegrationId(integrationId: number, onlyOpenRequests: boolean): Observable<VendorCatalogRequest[]> {
    return this.http.get<VendorCatalogRequest[]>(`${this.baseUrl}/integration/${integrationId}`, { params: { onlyOpenRequests } });
  }

  fetchPreparedRequest(integrationId: number): Observable<VendorCatalogRequest> {
  return this.http.get<VendorCatalogRequest>(`${this.baseUrl}/prepare`, {
    params: new HttpParams().set('integrationId', integrationId),
  });
}

  createRequest(request: VendorCatalogRequestWrite): Observable<VendorCatalogRequest> {
    return this.http.post<VendorCatalogRequest>(`${this.baseUrl}`, request);
  }

  createRequestMessage(requestMessage: VendorCatalogRequestMessageWrite, requestId: number): Observable<VendorCatalogRequestMessageWrite> {
    return this.http.post<VendorCatalogRequestMessageWrite>(`${this.baseUrl}/${requestId}/messages`, requestMessage);
  }

  markMessageAsRead(requestMessageId: number): Observable<boolean> {
    return this.http.put<boolean>(`${this.baseUrl}/message/${requestMessageId}/mark-as-read`, {});
  }

  closeRequest(requestId: number, acceptClient: boolean | null = null): Observable<boolean> {
    let httpParams = new HttpParams()
      .set('acceptClient', acceptClient ?? false)
    
    return this.http.post<boolean>(`${this.baseUrl}/${requestId}/close`, null, { params: httpParams });
  }

  reOpenRequest(requestId: number): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseUrl}/${requestId}/re-open`, {});
  }

  loadPagedRequestMessages(requestId: number, skip: number, take: number): Observable<VendorCatalogRequestMessage[]> {
     let httpParams = new HttpParams()
    .set('skip', skip)
    .set('take', take);

    return this.http.get<VendorCatalogRequestMessage[]>(
      `${this.baseUrl}/${requestId}/messages/paged`,
      { params: httpParams }
    );
  }

  fetchFilterOptions(params?: {
    integrationId?: number;
    clientId?: number;
    vendorId?: number;
    onlyAdmin?: boolean;
  }): Observable<{ clientNames: string[]; vendorNames: string[] }> {
    let httpParams = new HttpParams();
    if (params?.integrationId)
      httpParams = httpParams.set('integrationId', params.integrationId);
    if (params?.clientId)
      httpParams = httpParams.set('clientId', params.clientId);
    if (params?.vendorId)
      httpParams = httpParams.set('vendorId', params.vendorId);
    if (params?.onlyAdmin)
      httpParams = httpParams.set('onlyAdmin', params.onlyAdmin);

    return this.http.get<{ clientNames: string[]; vendorNames: string[] }>(
      `${this.baseUrl}/filter-options`,
      { params: httpParams }
    );
  }

  getOpenRequestCount(params?: {
    integrationId?: number;
    clientId?: number;
    vendorId?: number;
    onlyAdmin?: boolean;
  }): Observable<number> {
    let httpParams = new HttpParams();
    if (params?.integrationId)
      httpParams = httpParams.set('integrationId', params.integrationId);
    if (params?.clientId)
      httpParams = httpParams.set('clientId', params.clientId);
    if (params?.vendorId)
      httpParams = httpParams.set('vendorId', params.vendorId);
     if (params?.onlyAdmin)
      httpParams = httpParams.set('onlyAdmin', params.onlyAdmin);

    return this.http.get<number>(
      `${this.baseUrl}/open-requests/count`,
       {params: httpParams});
  }

  getUnreadRequestMessageCount(params?: {
    integrationId?: number;
    clientId?: number;
    vendorId?: number;
  }): Observable<number> {
    let httpParams = new HttpParams();
    if (params?.integrationId)
      httpParams = httpParams.set('integrationId', params.integrationId);
    if (params?.clientId)
      httpParams = httpParams.set('clientId', params.clientId);
    if (params?.vendorId)
      httpParams = httpParams.set('vendorId', params.vendorId);

    return this.http.get<number>(
      `${this.baseUrl}/unread-messages/count`,
       {params: httpParams});
  }
}
